/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { TFunction } from "i18next";
import {
  AppAlert,
  ErrorMessageValidator,
  SignUpFormValidator,
} from "@advapacs/common";
import { OrganisationIDBox } from "./OrganisationIDBox";

interface OrganisationInformationFormProps {
  t: TFunction;
  wrapperClass?: string;
  disabled?: boolean;
}

interface OrganisationInformationFormState {
  organisationId: string;
  organisationName: string;
  name: string;
  username: string;
}

const defaultOrganisationInformationFormState: Readonly<OrganisationInformationFormState> =
  {
    organisationId: "",
    organisationName: "",
    name: "",
    username: "",
  };
/**
 * Step 2: Information account.
 */
export const OrganisationInformationForm = forwardRef(
  (
    {
      t,
      wrapperClass = "",
      disabled = false,
    }: OrganisationInformationFormProps,
    ref
  ) => {
    const [model, setModel] = useState<OrganisationInformationFormState>(
      defaultOrganisationInformationFormState
    );

    const validator = useMemo(() => {
      return SignUpFormValidator(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, model]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setModel({
        ...model,
        [name]: name === "organisationId" ? value.toLowerCase() : value,
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        validate() {
          return {
            personalInformationValidator: validator,
          };
        },
        getData() {
          return { personalInformationModel: model };
        },
      }),
      [validator, model]
    );

    return (
      <div className={`personal-information ${wrapperClass}`}>
        <div className="form-group org-id">
          <label htmlFor="organisationId" className="col-form-label">
            {t("organisation_id")}
            <span className="text-danger">*</span>
          </label>
          <div className="form-group">
            <OrganisationIDBox
              t={t}
              disabled={disabled}
              organisationId={model.organisationId}
              setOrganisationId={(newId: string) =>
                setModel({ ...model, organisationId: newId })
              }
            />

            <ErrorMessageValidator
              errorMessage={validator.message(
                t("organisation_id"),
                model.organisationId,
                "required"
              )}
            />
          </div>
        </div>
        <div className="form-group">
          <AppAlert.SimpleAlert
            className="w-100  mb-0"
            variant="info"
            message={t("you_will_use_this_as_a_link_access_clinician_portal")}
          />
        </div>
        <div className="form-group">
          <label htmlFor="organisationName" className="col-form-label pb-1">
            {t("organisation_name")}
            <span className="text-danger">*</span>
          </label>
          <div className="form-group">
            <input
              disabled={disabled}
              type="text"
              value={model.organisationName}
              onChange={handleInputChange}
              className="form-control"
              placeholder={t("organisation_name")}
              name="organisationName"
            />
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("organisation_name"),
                model.organisationName,
                "required"
              )}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="name" className="col-form-label pb-1">
            {t("login:your_name")}
            <span className="text-danger">*</span>
          </label>
          <div className="form-group">
            <input
              disabled={disabled}
              type="text"
              value={model.name}
              onChange={handleInputChange}
              className="form-control"
              placeholder={t("login:your_name")}
              name="name"
              id="name"
            />
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("login:your_name"),
                model.name,
                "required|max:100"
              )}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="username" className="col-form-label pb-1">
            {t("login:username")}
            <span className="text-danger">*</span>
          </label>
          <div className="form-group">
            <input
              type="text"
              disabled={disabled}
              value={model.username}
              onChange={handleInputChange}
              className="form-control"
              placeholder={t("login:username")}
              name="username"
              id="username"
            />
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("login:username"),
                model.username,
                "required|userNameMinMax|userNamePattern"
              )}
            />
          </div>
        </div>
      </div>
    );
  }
);
